// The text a device-voice listening question reads aloud travels inside the question only in this masked form
// (UTF-8 bytes mixed with a small key, then base64), so it never appears as a label or as plain text in the page
// or in the question data. The browser unmasks it at the moment it plays. Grading never uses it: the answer is
// sealed in the question token and checked on the server.
// Shared by the server (masking) and the browser (unmasking); no Node or DOM-only APIs beyond TextEncoder, btoa, atob.

const KEY = [0x5a, 0x17, 0xc3, 0x8e, 0x21, 0x6b, 0xf4, 0x39, 0x0d, 0xa6]

const mix = (bytes: Uint8Array, salt: number): Uint8Array => {
    const out = new Uint8Array(bytes.length)

    for (let i = 0; i < bytes.length; i += 1) {
        out[i] = bytes[i] ^ KEY[(i + salt) % KEY.length] ^ ((salt * 31 + i * 7) & 0xff)
    }

    return out
}

/** Mask a text for the question payload. `salt` varies the result per question (any whole number). */
export const maskSpeech = (text: string, salt: number): string => {
    const s = Math.abs(Math.floor(salt)) & 0xff
    const bytes = mix(new TextEncoder().encode(text), s)
    let binary = ''

    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i])

    return `${s.toString(16).padStart(2, '0')}${btoa(binary)}`
}

/** The text behind a masked value, or null when it cannot be read. Call it only when the sound is played. */
export const unmaskSpeech = (masked: string): string | null => {
    if (typeof masked !== 'string' || masked.length < 3) return null

    try {
        const salt = parseInt(masked.slice(0, 2), 16)

        if (!Number.isFinite(salt)) return null

        const binary = atob(masked.slice(2))
        const bytes = new Uint8Array(binary.length)

        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)

        return new TextDecoder().decode(mix(bytes, salt)) || null
    }
    catch {
        return null
    }
}
